<script setup lang="ts">
import { computed } from 'vue';
import { IconName, IconSize, iconSizePx } from './icon.constants';
import { iconGlyphs } from './icon.glyphs';

type Props = {
  /** Which glyph to render. */
  name: IconName;
  /** Rendered size. */
  size?: IconSize;
};

const props = withDefaults(defineProps<Props>(), {
  size: IconSize.MD,
});

const px = computed(() => iconSizePx[props.size]);

const glyph = computed(() => {
  const definition = iconGlyphs[props.name];

  return Array.isArray(definition)
    ? { viewBox: '0 0 24 24', paths: definition }
    : definition;
});

const paths = computed(() =>
  glyph.value.paths.map((path) => {
    if (typeof path === 'string') {
      return { d: path };
    }

    const filled = path.filled ?? false;
    const stroked = path.stroked ?? !filled;
    const fillRule = filled ? (path.fillRule ?? 'evenodd') : undefined;

    return {
      d: path.d,
      fill: filled ? 'currentColor' : undefined,
      stroke: stroked ? undefined : 'none',
      'fill-rule': fillRule,
      'clip-rule': fillRule,
      'stroke-width': path.strokeWidth,
      'stroke-linecap': path.linecap,
      'stroke-linejoin': path.linejoin,
      opacity: path.opacity,
      transform: path.transform,
    };
  }),
);
</script>

<template>
  <svg
    :width="px"
    :height="px"
    :viewBox="glyph.viewBox"
    fill="none"
    stroke="currentColor"
    stroke-width="1.5"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
    class="inline-block shrink-0"
  >
    <path v-for="(path, i) in paths" :key="i" v-bind="path" />
  </svg>
</template>
